import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, AlertTriangle } from 'lucide-react';

import { ProfileTabs, ReadonlyRow, StateChip } from '@/components/chassis/ProfileTabs';
import { ErrorState, Skeleton } from '@/components/chassis/States';
import { meApi } from '@/features/me/me.api';
import { auditApi } from '@/features/audit/audit.api';
import { actionCategory } from '@/features/audit/audit-actions';
import { humanizeAction, humanizeEntity } from '@/features/audit/humanize';
import { useSystemConfigList } from '@/features/system-config/system-config.hooks';
import { ApiError } from '@/lib/api-client';
import { useAuthStore } from '@/stores/auth.store';
import { useLanguageStore } from '@/stores/language.store';
import { toast } from '@/stores/toast.store';
import { useT, useLang, type StringKey } from '@/i18n';
import { cn } from '@/lib/utils';

/**
 * F5-T6c · Admin 13 — «Mi perfil» (contrato 2026-08-28).
 *
 * Tres pestañas (Datos · Seguridad · Actividad), estado por ETIQUETA y no por
 * color de fondo, un solo primario por pestaña. Las pestañas son EL chasis
 * ProfileTabs de T2-5 (doctrina «un paginador es EL Pager aunque el dibujo
 * tenga chevrons»: el dibujo trae cápsula navy, el chasis trae subrayado —
 * desviación declarada en el reporte). La pantalla es la ruta admin
 * /admin/profile; la SettingsPage compartida de los otros roles NO se toca.
 *
 * MUERE por contrato: la edición del PROPIO correo desde esta pantalla (el
 * correo pasa a solo-lectura con la razón al lado; lo cambia otro admin desde
 * el Centro de equipo — la maquinaria PATCH del server queda, la UI ya no la
 * ofrece) · el rol editable (nunca lo fue) · el rojo de UI.
 *
 * SE CONSERVA con su maquinaria viva EXACTA: cambio de contraseña (misma
 * validación de política, mismos errores pegados al campo, mismo endpoint) ·
 * el checklist vivo de requisitos (control vivo ni dibujado ni matado, E-23)
 * · la pantalla de inscripción MFA existente (/settings/mfa/enroll) como
 * destino del botón cuando el sistema la exige.
 *
 * NECESITA-SERVER:
 * - «Mis sesiones abiertas»: no existe endpoint de sesiones → se muestra
 *   SOLO la sesión actual (navegador real + last login del perfil), sin
 *   «Cerrarla», con el diagnóstico al pie. El conteo del rótulo es honesto: 1.
 * - Equipo del usuario en el subtítulo («· Bronx ·»): el perfil no trae
 *   equipo → el segmento se omite.
 * - «Al cambiarla se cierran todas tus otras sesiones» (copy del contrato):
 *   VERIFICAR en server que el cambio de contraseña revoque las otras
 *   sesiones; si no lo hace, este aviso miente y hay que cablearlo.
 */

const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;

function getInitials(fullName: string): string {
  const parts = fullName.trim().split(/\s+/);
  const first = parts[0] ?? '';
  if (first === '') return '?';
  if (parts.length === 1) return first.slice(0, 2).toUpperCase();
  const last = parts[parts.length - 1] ?? first;
  return ((first[0] ?? '') + (last[0] ?? '')).toUpperCase() || '?';
}

function roleLabel(role: string, t: ReturnType<typeof useT>): string {
  const key = `f5t6c.role.${role}` as StringKey;
  const label = t(key);
  return label === key ? role : label;
}

function parseUa(ua: string): string {
  const os = /Windows/i.test(ua)
    ? 'Windows'
    : /Mac OS X|Macintosh/i.test(ua)
      ? 'macOS'
      : /Android/i.test(ua)
        ? 'Android'
        : /iPhone|iPad/i.test(ua)
          ? 'iOS'
          : /Linux/i.test(ua)
            ? 'Linux'
            : '—';
  const edge = ua.match(/Edg\/(\d+)/);
  const chrome = ua.match(/Chrome\/(\d+)/);
  const firefox = ua.match(/Firefox\/(\d+)/);
  const browser = edge
    ? `Edge ${edge[1]}`
    : chrome
      ? `Chrome ${chrome[1]}`
      : firefox
        ? `Firefox ${firefox[1]}`
        : 'Browser';
  return `${os} · ${browser}`;
}

type Tab = 'datos' | 'seguridad' | 'actividad';

export function MyProfilePage(): JSX.Element {
  const t = useT();
  const [tab, setTab] = useState<Tab>('datos');

  const profileQ = useQuery({
    queryKey: ['me', 'profile'],
    queryFn: () => meApi.get(),
    staleTime: 30_000,
  });

  const lang = useLang();
  const locale = lang === 'es' ? 'es' : 'en';
  const p = profileQ.data;

  const since = p?.createdAt
    ? new Date(p.createdAt).toLocaleDateString(locale, { month: 'long', year: 'numeric' })
    : null;

  return (
    <div className="max-w-[1440px]">
      <section className="bg-nav-soft border-[1.5px] border-nav-card rounded-[14px] px-8 pt-7 pb-[34px] animate-fade-up-slow motion-reduce:animate-none">
        {profileQ.isLoading ? (
          <div className="space-y-4" aria-busy="true">
            <div className="flex items-center gap-4">
              <Skeleton className="w-16 h-16 rounded-full" />
              <div className="space-y-2">
                <Skeleton className="h-6 w-56" />
                <Skeleton className="h-4 w-72" />
              </div>
            </div>
            <Skeleton className="h-11 w-80" />
            <Skeleton className="h-64 w-full max-w-[620px]" />
          </div>
        ) : profileQ.error || !p ? (
          <div className="bg-white border-[1.5px] border-nav-card rounded-[12px]">
            <ErrorState
              icon={<AlertCircle className="h-5 w-5" aria-hidden />}
              title={t('f5t6c.profile.act.error.title')}
              body={t('f5t6c.profile.act.error.body')}
              action={{ label: t('f5t6c.profile.act.error.retry'), onClick: () => profileQ.refetch() }}
            />
          </div>
        ) : (
          <>
            {/* Cabecera de identidad: iniciales sobre navy (la foto de perfil
                MUERE por contrato), rol visible y jamás editable. */}
            <div className="flex items-center gap-4 flex-wrap mb-6">
              <span
                aria-hidden
                className="w-16 h-16 flex-none rounded-full bg-nav-navy text-white inline-flex items-center justify-center font-head text-[21px] font-bold tracking-[.02em]"
              >
                {getInitials(p.fullName)}
              </span>
              <div className="min-w-0">
                <h1 className="font-head text-[22px] font-extrabold tracking-[-0.02em] text-nav-navy m-0 truncate">
                  {p.fullName}
                </h1>
                {/* NECESITA-SERVER: equipo del usuario — el perfil no lo trae,
                    el segmento «· {equipo}» se omite. */}
                <p className="font-body text-[13.5px] text-nav-slate m-0 mt-0.5">
                  {roleLabel(p.role, t)}
                  {since ? ` · ${t('f5t6c.profile.since', { when: since })}` : ''}
                </p>
              </div>
            </div>

            <div className="mb-5">
              <ProfileTabs
                tabs={[
                  { id: 'datos', label: t('f5t6c.profile.tab.datos') },
                  { id: 'seguridad', label: t('f5t6c.profile.tab.seguridad') },
                  { id: 'actividad', label: t('f5t6c.profile.tab.actividad') },
                ]}
                active={tab}
                onTab={(id) => setTab(id as Tab)}
              />
            </div>

            {tab === 'datos' && <DatosTab t={t} fullName={p.fullName} email={p.email} />}
            {tab === 'seguridad' && (
              <SeguridadTab t={t} mfaEnabled={p.mfaEnabled} lastLoginAt={p.lastLoginAt ?? null} locale={locale} />
            )}
            {tab === 'actividad' && <ActividadTab t={t} locale={locale} />}
          </>
        )}
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------

function DatosTab({
  t,
  fullName,
  email,
}: {
  t: ReturnType<typeof useT>;
  fullName: string;
  email: string;
}): JSX.Element {
  const qc = useQueryClient();
  const authUser = useAuthStore((s) => s.user);
  const lang = useLanguageStore((s) => s.lang);
  const setLang = useLanguageStore((s) => s.setLang);

  const [name, setName] = useState(fullName);
  const [nameErr, setNameErr] = useState<string | null>(null);
  const [liveMsg, setLiveMsg] = useState('');

  useEffect(() => setName(fullName), [fullName]);

  const save = useMutation({
    // Maquinaria conservada: PATCH /me/profile — solo viaja fullName (la
    // edición del propio correo MUERE por contrato).
    mutationFn: (input: { fullName: string }) => meApi.update(input),
    onSuccess: (u) => {
      qc.setQueryData(['me', 'profile'], u);
      if (authUser) {
        useAuthStore.setState({ user: { ...authUser, fullName: u.fullName } });
      }
      setLiveMsg(t('f5t6c.profile.saved'));
      toast.success(t('f5t6c.profile.saved'), '');
    },
  });

  const dirty = name.trim() !== fullName && name.trim().length > 0;

  const submit = (): void => {
    setNameErr(null);
    const trimmed = name.trim();
    if (trimmed.length < 1 || trimmed.length > 100) {
      setNameErr(t('admin.profile.fullNameRequired'));
      return;
    }
    save.mutate({ fullName: trimmed });
  };

  const pillCls = (active: boolean): string =>
    cn(
      'inline-flex items-center min-h-[44px] px-[18px] rounded-lg border-[1.5px] font-head text-[13px] font-bold transition-colors duration-150',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal focus-visible:ring-offset-2',
      active
        ? 'bg-nav-navy border-nav-navy text-white hover:bg-nav-navyHover'
        : 'bg-white border-nav-line text-nav-navy hover:bg-[#f4fafa] hover:text-nav-tealDark hover:border-nav-teal',
    );

  return (
    <div className="bg-white border-[1.5px] border-nav-card rounded-[12px] px-[26px] py-6 max-w-[620px]">
      <p aria-live="polite" role="status" className="sr-only">
        {liveMsg}
      </p>

      <div className="grid grid-cols-[repeat(auto-fit,minmax(230px,1fr))] gap-x-5 gap-y-[18px]">
        <label className="block">
          <span className="block font-head text-[10.5px] font-bold tracking-[0.12em] uppercase text-nav-slate">
            {t('f5t6c.profile.name')}
          </span>
          <input
            type="text"
            value={name}
            maxLength={100}
            onChange={(e) => setName(e.target.value)}
            aria-invalid={!!nameErr}
            aria-describedby={nameErr ? 'profile-name-err' : undefined}
            className="w-full box-border h-11 mt-[7px] px-[13px] border-[1.5px] border-nav-line rounded-lg font-body text-[14px] text-nav-navy bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal"
          />
          {nameErr && (
            <span id="profile-name-err" className="block mt-1 font-body text-[12px] text-nav-red">
              {nameErr}
            </span>
          )}
        </label>
        {/* Lo no-editable se muestra SIN campo, con la razón al lado —
            ReadonlyRow del chasis T2-5 (la edición del propio correo MUERE). */}
        <div className="min-w-0 self-end">
          <ReadonlyRow label={t('f5t6c.profile.email')} value={email} reason={t('f5t6c.profile.emailNote')} />
        </div>
      </div>

      <div className="mt-5">
        <span className="block font-head text-[10.5px] font-bold tracking-[0.12em] uppercase text-nav-slate">
          {t('f5t6c.profile.lang')}
        </span>
        {/* UNA sola preferencia con el toggle EN/ES del chrome (se conserva):
            estas pills escriben el MISMO language store. */}
        <div className="flex items-center gap-2 mt-[9px]">
          <button type="button" aria-pressed={lang === 'es'} onClick={() => setLang('es')} className={pillCls(lang === 'es')}>
            {t('f5t6c.profile.langEs')}
          </button>
          <button type="button" aria-pressed={lang === 'en'} onClick={() => setLang('en')} className={pillCls(lang === 'en')}>
            {t('f5t6c.profile.langEn')}
          </button>
        </div>
        <p className="font-body text-[12.5px] leading-[1.55] text-nav-slate mt-2 m-0">
          {t('f5t6c.profile.langNote')}
        </p>
      </div>

      {save.error && (
        <p role="alert" className="font-body text-[13px] text-nav-red mt-3 m-0">
          {save.error instanceof ApiError && save.error.status === 409
            ? t('admin.profile.emailInUse')
            : (save.error as Error).message}
        </p>
      )}

      <div className="flex justify-end gap-2.5 mt-6">
        <button
          type="button"
          onClick={() => {
            setName(fullName);
            setNameErr(null);
          }}
          disabled={!dirty || save.isPending}
          className="min-h-[44px] px-[18px] border-[1.5px] border-nav-line rounded-lg bg-white font-head text-[13.5px] font-bold text-nav-navy hover:bg-[#f2f5f7] hover:border-nav-faint disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal"
        >
          {t('f5t6c.profile.discard')}
        </button>
        {/* Un solo primario por pestaña; inerte sin cambios. */}
        <button
          type="button"
          onClick={submit}
          disabled={!dirty || save.isPending}
          className="min-h-[44px] px-5 bg-nav-navy text-white border-[1.5px] border-nav-navy rounded-lg font-head text-[13.5px] font-bold hover:bg-nav-navyHover disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal focus-visible:ring-offset-2"
        >
          {save.isPending ? t('f5t6c.profile.saving') : t('f5t6c.profile.save')}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function SeguridadTab({
  t,
  mfaEnabled,
  lastLoginAt,
  locale,
}: {
  t: ReturnType<typeof useT>;
  mfaEnabled: boolean;
  lastLoginAt: string | null;
  locale: string;
}): JSX.Element {
  return (
    <div className="grid grid-cols-[repeat(auto-fit,minmax(320px,1fr))] gap-4 max-w-[1000px] items-start">
      <PasswordCard t={t} />
      <div className="flex flex-col gap-4">
        <MfaCard t={t} mfaEnabled={mfaEnabled} />
        <SessionsCard t={t} lastLoginAt={lastLoginAt} locale={locale} />
      </div>
    </div>
  );
}

function PasswordCard({ t }: { t: ReturnType<typeof useT> }): JSX.Element {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [errs, setErrs] = useState<{ next?: string; confirm?: string; current?: string }>({});
  const [serverErr, setServerErr] = useState<string | null>(null);

  const mutation = useMutation({
    // Maquinaria conservada EXACTA: POST /me/change-password.
    mutationFn: () => meApi.changePassword({ currentPassword: current, newPassword: next }),
    onSuccess: () => {
      setCurrent('');
      setNext('');
      setConfirm('');
      setErrs({});
      setServerErr(null);
      toast.success(t('admin.password.saved'), t('admin.password.savedDesc'));
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 401) {
        setErrs({ current: t('admin.password.wrongCurrent') });
        setServerErr(null);
      } else {
        setServerErr((err as Error).message);
      }
    },
  });

  const submit = (e: React.FormEvent): void => {
    e.preventDefault();
    const newErrs: typeof errs = {};
    if (!PASSWORD_REGEX.test(next)) newErrs.next = t('admin.password.policyFail');
    // Error pegado al campo, no toast lejano (contrato states).
    if (next !== confirm) newErrs.confirm = t('admin.password.mismatch');
    if (current.length < 1) newErrs.current = t('admin.password.wrongCurrent');
    setErrs(newErrs);
    setServerErr(null);
    if (Object.keys(newErrs).length > 0) return;
    mutation.mutate();
  };

  const reqs = {
    minLength: next.length >= 8,
    upperLower: /[a-z]/.test(next) && /[A-Z]/.test(next),
    digit: /\d/.test(next),
  };

  const field = (
    id: string,
    label: string,
    value: string,
    setValue: (v: string) => void,
    err: string | undefined,
    autoComplete: string,
  ): JSX.Element => (
    <label className="block mb-4" htmlFor={id}>
      <span className="block font-head text-[10.5px] font-bold tracking-[0.12em] uppercase text-nav-slate">
        {label}
      </span>
      <input
        id={id}
        type="password"
        autoComplete={autoComplete}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        aria-invalid={!!err}
        aria-describedby={err ? `${id}-err` : undefined}
        className={cn(
          'w-full box-border h-11 mt-[7px] px-[13px] border-[1.5px] rounded-lg font-body text-[14px] text-nav-navy bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal',
          err ? 'border-nav-red' : 'border-nav-line',
        )}
      />
      {err && (
        <span id={`${id}-err`} className="block mt-1 font-body text-[12px] text-nav-red">
          {err}
        </span>
      )}
    </label>
  );

  return (
    <form onSubmit={submit} noValidate className="bg-white border-[1.5px] border-nav-card rounded-[12px] px-[26px] py-6">
      <p className="font-head text-[10.5px] font-bold tracking-[.14em] uppercase text-nav-teal m-0 mb-4">
        {t('f5t6c.profile.pw.title')}
      </p>

      {field('pw-current', t('f5t6c.profile.pw.current'), current, setCurrent, errs.current, 'current-password')}
      {field('pw-new', t('f5t6c.profile.pw.new'), next, setNext, errs.next, 'new-password')}
      {field('pw-confirm', t('f5t6c.profile.pw.repeat'), confirm, setConfirm, errs.confirm, 'new-password')}

      {/* Conservado (E-23): el checklist vivo de requisitos de la política. */}
      {next.length > 0 && (
        <ul className="m-0 mb-2 p-0 list-none space-y-1">
          {(
            [
              [reqs.minLength, t('admin.password.requirements.minLength')],
              [reqs.upperLower, t('admin.password.requirements.upperLower')],
              [reqs.digit, t('admin.password.requirements.digit')],
            ] as Array<[boolean, string]>
          ).map(([ok, label]) => (
            <li key={label} className={cn('font-body text-[12px]', ok ? 'text-nav-sageText' : 'text-nav-slate')}>
              {ok ? '✓' : '·'} {label}
            </li>
          ))}
        </ul>
      )}

      {serverErr && (
        <p role="alert" className="font-body text-[13px] text-nav-red m-0 mb-2">
          {serverErr}
        </p>
      )}

      {/* La CONSECUENCIA dicha ANTES del punto de acción (caja ámbar).
          NECESITA-SERVER (verificación): confirmar que el cambio revoca las
          otras sesiones; si no, este aviso hay que cablearlo del lado server. */}
      <div className="flex items-start gap-2.5 mt-[18px] bg-nav-amberTint border-[1.5px] border-nav-amber rounded-[12px] py-[15px] px-[17px] text-nav-amberText">
        <AlertTriangle className="h-[17px] w-[17px] flex-none mt-px" aria-hidden />
        <p className="font-body text-[13.5px] leading-[1.55] m-0">
          {t('f5t6c.profile.pw.warn1')}
          <strong className="font-bold">{t('f5t6c.profile.pw.warnBold')}</strong>
          {t('f5t6c.profile.pw.warn2')}
        </p>
      </div>

      <div className="flex justify-end mt-5">
        <button
          type="submit"
          disabled={mutation.isPending}
          className="min-h-[44px] px-5 bg-nav-navy text-white border-[1.5px] border-nav-navy rounded-lg font-head text-[13.5px] font-bold hover:bg-nav-navyHover disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal focus-visible:ring-offset-2"
        >
          {mutation.isPending ? t('f5t6c.profile.saving') : t('f5t6c.profile.pw.submit')}
        </button>
      </div>
    </form>
  );
}

function MfaCard({
  t,
  mfaEnabled,
}: {
  t: ReturnType<typeof useT>;
  mfaEnabled: boolean;
}): JSX.Element {
  const navigate = useNavigate();
  // La clave real del sistema (admin-only): mfa_required_roles. Si la lista
  // está vacía, el MFA está APAGADO EN EL SISTEMA — gris a propósito,
  // «apagado no es un aviso», jamás ámbar/rojo.
  const configQ = useSystemConfigList();
  const requiredRoles = useMemo(() => {
    const item = (configQ.data?.items ?? []).find((i) => i.key === 'mfa_required_roles');
    const v = item?.value;
    if (Array.isArray(v)) return v as string[];
    if (typeof v === 'string') {
      try {
        const parsed = JSON.parse(v) as unknown;
        return Array.isArray(parsed) ? (parsed as string[]) : [];
      } catch {
        return [];
      }
    }
    return [];
  }, [configQ.data]);

  const systemOn = requiredRoles.length > 0;
  const state: 'active' | 'pending' | 'off' = mfaEnabled ? 'active' : systemOn ? 'pending' : 'off';

  return (
    <div className="bg-white border-[1.5px] border-nav-card rounded-[12px] px-[26px] py-6">
      <div className="flex items-center justify-between gap-3 mb-3">
        <p className="font-head text-[10.5px] font-bold tracking-[.14em] uppercase text-nav-teal m-0">
          {t('f5t6c.profile.mfa.title')}
        </p>
        {state === 'active' ? (
          <span className="inline-flex items-center rounded-full border border-[#c9e2e2] bg-nav-tealTint text-nav-tealDark px-[11px] py-1 font-head text-[11.5px] font-bold">
            {t('f5t6c.profile.mfa.activeChip')}
          </span>
        ) : state === 'pending' ? (
          <span className="inline-flex items-center rounded-full border border-[#e6cf9a] bg-nav-amberTint text-nav-amberText px-[11px] py-1 font-head text-[11.5px] font-bold">
            {t('f5t6c.profile.mfa.pendingChip')}
          </span>
        ) : (
          <StateChip>{t('f5t6c.profile.mfa.offChip')}</StateChip>
        )}
      </div>

      <p className="font-body text-[13.5px] leading-[1.6] text-nav-slate m-0 mb-4">
        {state === 'active'
          ? t('f5t6c.profile.mfa.activeBody')
          : state === 'pending'
            ? t('f5t6c.profile.mfa.pendingBody')
            : t('f5t6c.profile.mfa.offBody')}
      </p>

      {state !== 'active' && (
        <>
          <button
            type="button"
            disabled={state === 'off'}
            aria-describedby={state === 'off' ? 'mfa-off-why' : undefined}
            onClick={() => {
              if (state === 'pending') navigate('/settings/mfa/enroll');
            }}
            className={cn(
              'inline-flex items-center min-h-[44px] px-[18px] rounded-lg border-[1.5px] font-head text-[13.5px] font-bold',
              state === 'off'
                ? 'bg-[#f2f5f7] border-nav-card text-nav-faint cursor-not-allowed'
                : 'bg-nav-navy border-nav-navy text-white hover:bg-nav-navyHover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal focus-visible:ring-offset-2',
            )}
          >
            {t('f5t6c.profile.mfa.enroll')}
          </button>
          {state === 'off' && (
            // Patrón «diagnóstico del deshabilitado»: el porqué y dónde vive
            // la clave, al lado del botón.
            <p id="mfa-off-why" className="font-body text-[12.5px] leading-[1.55] text-nav-slate mt-2 m-0">
              {t('f5t6c.profile.mfa.offWhy')}
            </p>
          )}
        </>
      )}
    </div>
  );
}

function SessionsCard({
  t,
  lastLoginAt,
  locale,
}: {
  t: ReturnType<typeof useT>;
  lastLoginAt: string | null;
  locale: string;
}): JSX.Element {
  // NECESITA-SERVER: la lista real de sesiones y «Cerrarla». Hoy solo se
  // puede decir la verdad de ESTA sesión: el navegador propio (dato real del
  // user-agent) y el último ingreso que el perfil ya trae.
  const device = parseUa(navigator.userAgent);
  const opened = lastLoginAt
    ? new Date(lastLoginAt).toLocaleString(locale, {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      })
    : null;

  return (
    <div className="bg-white border-[1.5px] border-nav-card rounded-[12px] px-[26px] py-6">
      <p className="font-head text-[10.5px] font-bold tracking-[.14em] uppercase text-nav-teal m-0 mb-3">
        {t('f5t6c.profile.sessions.title', { n: 1 })}
      </p>
      <div className="flex items-center gap-3 min-h-[56px] px-3.5 py-[9px] bg-[#fafbfc] border-[1.5px] border-nav-card rounded-[10px]">
        <span className="flex-1 min-w-0">
          <span className="block font-head text-[13px] font-bold text-nav-navy truncate">{device}</span>
          {opened && (
            <span className="block font-body text-[12px] text-nav-slate tabular-nums">
              {t('f5t6c.profile.sessions.open', { when: opened })}
            </span>
          )}
        </span>
        {/* Sin botón de cerrar la sesión actual: «es una trampa» (contrato). */}
        <span className="flex-none inline-flex items-center rounded-full border border-[#c9e2e2] bg-nav-tealTint text-nav-tealDark px-2.5 py-1 font-head text-[11px] font-bold">
          {t('f5t6c.profile.sessions.this')}
        </span>
      </div>
      <p className="font-body text-[12.5px] leading-[1.55] text-nav-slate mt-2 m-0">
        {t('f5t6c.profile.sessions.note')}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------

function ActividadTab({ t, locale }: { t: ReturnType<typeof useT>; locale: string }): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const lang = useLang();

  // El MISMO registro de auditoría del Registro de auditoría (Admin 09),
  // filtrado por el usuario propio — solo lectura, sin edición ni borrado.
  const actQ = useQuery({
    queryKey: ['me', 'activity', user?.email],
    queryFn: () => auditApi.list({ userQuery: user?.email ?? '' }, 8, 0),
    enabled: !!user?.email,
    staleTime: 30_000,
  });

  // Catálogo de color de los dots (contrato, decisions 5): config/lista=navy ·
  // export PHI=ámbar · usuario (desactivación)=rojo · consulta=teal · resto slate.
  const dotFor = (action: string): string => {
    const cat = actionCategory(action);
    if (cat === 'system' || cat === 'batch') return 'bg-nav-navy';
    if (cat === 'report' || cat === 'phi') return 'bg-nav-amber';
    if (cat === 'user') return 'bg-nav-red';
    if (cat === 'screenings') return 'bg-nav-teal';
    return 'bg-nav-slate';
  };

  const stamp = (iso: string): string => {
    const d = new Date(iso);
    const time = d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
    const day = d.toLocaleDateString(locale, { day: '2-digit', month: 'short' });
    return `${day} ${time}`;
  };

  return (
    <div className="bg-white border-[1.5px] border-nav-card rounded-[12px] px-[26px] py-6 max-w-[760px]">
      <p className="font-head text-[10.5px] font-bold tracking-[.14em] uppercase text-nav-teal m-0 mb-2">
        {t('f5t6c.profile.act.title')}
      </p>
      <p className="font-body text-[13.5px] text-nav-slate m-0 mb-4">{t('f5t6c.profile.act.intro')}</p>

      {actQ.isLoading ? (
        <div className="space-y-2.5" aria-busy="true">
          {[0, 1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-[52px] w-full rounded-[10px]" />
          ))}
        </div>
      ) : actQ.error ? (
        <ErrorState
          icon={<AlertCircle className="h-5 w-5" aria-hidden />}
          title={t('f5t6c.profile.act.error.title')}
          body={t('f5t6c.profile.act.error.body')}
          action={{ label: t('f5t6c.profile.act.error.retry'), onClick: () => actQ.refetch() }}
        />
      ) : (actQ.data?.items.length ?? 0) === 0 ? (
        <p className="font-body text-[13.5px] text-nav-slate m-0 py-4">{t('f5t6c.profile.act.empty')}</p>
      ) : (
        <ul className="m-0 p-0 list-none space-y-2.5">
          {actQ.data?.items.map((e) => (
            <li
              key={e.id}
              className="flex items-center gap-3 min-h-[52px] px-3.5 py-[9px] bg-[#fafbfc] border-[1.5px] border-nav-card rounded-[10px]"
            >
              <span aria-hidden className={cn('w-2.5 h-2.5 rounded-full flex-none', dotFor(e.action))} />
              <span className="flex-1 min-w-0 font-body text-[13.5px] leading-[1.5] text-nav-navy">
                {humanizeAction(e.action, lang)}
                {e.entityType ? ` · ${humanizeEntity(e.entityType, lang)}` : ''}
                {e.entityId ? ` #${e.entityId}` : ''}
              </span>
              <span className="flex-none font-head text-[12.5px] font-bold text-nav-slate tabular-nums">
                {stamp(e.occurredAt)}
              </span>
            </li>
          ))}
        </ul>
      )}

      <p className="font-body text-[12.5px] leading-[1.55] text-nav-slate mt-[18px] m-0">
        {t('f5t6c.profile.act.close')}
      </p>
    </div>
  );
}
